/**
 * How a company reads its receivables — Timely's interpretation, never the
 * accounting (docs/tally/18 §7–§10).
 *
 * Everything here changes how figures are *grouped, labelled and
 * prioritised*. Nothing here can change a fact: a voucher amount, a balance,
 * a bill's reference or dates, a receipt, a credit, the company's identity,
 * or an allocation TallyPrime states. `SOURCE_FACTS_NOT_CUSTOMIZABLE` lists
 * those, and a test holds every setting in `SETTINGS_INVENTORY` to
 * `affectsSourceTruth: false`.
 *
 * Settings are versioned: each save is a new version, never an edit of the
 * last (migration 0036, `receivables_settings`). Changing a setting
 * recomputes the presentation from the same facts; history, notes and
 * promises are untouched.
 */
import {
    DEFAULT_AGEING, bucketLabels, validateBoundaries,
    type AgeingConfig, type AgeingProblem, type MissingDueDateRule,
} from './ageingEngine.js';

export const RECEIVABLES_SETTINGS_SCHEMA = 1 as const;

/**
 * Which customer ledgers count. Everything the connector reads (the ledgers
 * under Sundry Debtors, with its sub-groups) is in unless excluded here — so
 * a debtor can never be left out by omission, only by a rule someone chose
 * and can see.
 */
export interface ScopeRules {
    /** Ledger groups excluded (matched by name, as TallyPrime spells it). */
    excludeGroups: string[];
    /** Ledgers excluded, by their identity in the book (`tally:<guid>`). */
    excludeAccounts: string[];
}

/** Thresholds the collection priority reads (`collectionPriority.ts`). Never a hidden score. */
export interface CollectionSettings {
    /** An account owing at least this much is high value. Null: no account is called high value. */
    highValueAmount: number | null;
    /** Money more than this many days past its due date is critical. */
    criticalOverdueDays: number;
    /** No recorded contact for this many days is worth a call. */
    staleContactDays: number;
    /** A promise still unanswered this many days after its date counts as missed. */
    promiseGraceDays: number;
}

export interface ReceivablesSettings {
    schema: typeof RECEIVABLES_SETTINGS_SCHEMA;
    ageing: AgeingConfig;
    /**
     * Days after a bill's due date before Timely treats it as overdue for
     * collections. TallyPrime's due date is shown unchanged; only Timely's
     * "overdue for collections" waits.
     */
    graceDays: number;
    scope: ScopeRules;
    collection: CollectionSettings;
}

export const SETTINGS_LIMITS = {
    graceDays: 90,
    thresholdDays: 3650,
    excludedGroups: 50,
    excludedAccounts: 2000,
    amount: 1e13,
} as const;

export const DEFAULT_RECEIVABLES_SETTINGS: ReceivablesSettings = {
    schema: RECEIVABLES_SETTINGS_SCHEMA,
    ageing: DEFAULT_AGEING,
    graceDays: 0,
    scope: { excludeGroups: [], excludeAccounts: [] },
    collection: { highValueAmount: null, criticalOverdueDays: 90, staleContactDays: 14, promiseGraceDays: 0 },
};

export interface SettingsProblem { path: string; message: string }

const isObject = (v: unknown): v is Record<string, unknown> => typeof v === 'object' && v !== null && !Array.isArray(v);
const wholeIn = (v: unknown, min: number, max: number): v is number => typeof v === 'number' && Number.isInteger(v) && v >= min && v <= max;

/**
 * Settings designed and checked but not shipped (docs/tally/18 §7): leaving
 * ledgers or groups out, and the collection thresholds. Customers, Today and
 * Reports do not act on them yet, so no company may set them — refused here
 * for the API, and again by the database (`receivables_settings_valid`). Until
 * they ship they stay at the product's defaults.
 */
export function unshippedSettings(settings: ReceivablesSettings): SettingsProblem[] {
    const problems: SettingsProblem[] = [];
    if (settings.scope.excludeGroups.length || settings.scope.excludeAccounts.length) {
        problems.push({ path: 'scope', message: 'Leaving ledgers out is not available yet: Customers, Today and Reports would still treat them as active.' });
    }
    const c = settings.collection, d = DEFAULT_RECEIVABLES_SETTINGS.collection;
    if (c.highValueAmount !== d.highValueAmount || c.criticalOverdueDays !== d.criticalOverdueDays
        || c.staleContactDays !== d.staleContactDays || c.promiseGraceDays !== d.promiseGraceDays) {
        problems.push({ path: 'collection', message: 'Collection thresholds are not available yet: nothing in Timely acts on them.' });
    }
    return problems;
}

/**
 * Settings as saved, checked strictly. Anything wrong refuses the whole save
 * with every problem named; nothing is repaired or defaulted silently.
 */
export function validateReceivablesSettings(input: unknown): { settings: ReceivablesSettings | null; problems: SettingsProblem[] } {
    const problems: SettingsProblem[] = [];
    const bad = (path: string, message: string) => { if (problems.length < 50) problems.push({ path, message }); };
    if (!isObject(input)) return { settings: null, problems: [{ path: '', message: 'Settings must be an object.' }] };
    if (input['schema'] !== RECEIVABLES_SETTINGS_SCHEMA) bad('schema', `must be ${RECEIVABLES_SETTINGS_SCHEMA}`);

    const ageingRaw = isObject(input['ageing']) ? input['ageing'] : null;
    if (!ageingRaw) bad('ageing', 'is required');
    const basis = ageingRaw?.['basis'];
    if (basis !== 'bill_date' && basis !== 'due_date') bad('ageing.basis', 'must be bill_date or due_date');
    const missing = ageingRaw?.['missingDueDate'];
    if (missing !== 'use_bill_date' && missing !== 'unaged') bad('ageing.missingDueDate', 'must be use_bill_date or unaged');
    const boundaryProblems: AgeingProblem[] = basis === 'bill_date' || basis === 'due_date'
        ? validateBoundaries(ageingRaw?.['boundaries'], basis) : [];
    for (const p of boundaryProblems) bad(`ageing.boundaries${p.bucket === undefined ? '' : `[${p.bucket}]`}`, p.message);

    if (!wholeIn(input['graceDays'], 0, SETTINGS_LIMITS.graceDays)) bad('graceDays', `must be a whole number of days from 0 to ${SETTINGS_LIMITS.graceDays}`);

    const scopeRaw = isObject(input['scope']) ? input['scope'] : null;
    if (!scopeRaw) bad('scope', 'is required');
    const names = (v: unknown, path: string, max: number, maxLen: number): string[] => {
        if (!Array.isArray(v)) { bad(path, 'must be a list'); return []; }
        if (v.length > max) bad(path, `at most ${max}`);
        const out: string[] = [];
        v.slice(0, max).forEach((s, i) => {
            // eslint-disable-next-line no-control-regex
            if (typeof s !== 'string' || !s.trim() || s.length > maxLen || /[\u0000-\u001F]/.test(s)) bad(`${path}[${i}]`, 'is not a name');
            else if (out.some(o => o.toLowerCase() === s.trim().toLowerCase())) bad(`${path}[${i}]`, 'appears twice');
            else out.push(s.trim());
        });
        return out;
    };
    const excludeGroups = scopeRaw ? names(scopeRaw['excludeGroups'], 'scope.excludeGroups', SETTINGS_LIMITS.excludedGroups, 200) : [];
    const excludeAccounts = scopeRaw ? names(scopeRaw['excludeAccounts'], 'scope.excludeAccounts', SETTINGS_LIMITS.excludedAccounts, 120) : [];
    excludeAccounts.forEach((k, i) => { if (!/^tally:[a-z0-9][a-z0-9-]{7,79}$/.test(k)) bad(`scope.excludeAccounts[${i}]`, 'is not a ledger identity'); });
    if (excludeGroups.some(g => g.toLowerCase() === 'sundry debtors')) bad('scope.excludeGroups', 'Sundry Debtors itself cannot be excluded: that would leave out every customer');

    const collectionRaw = isObject(input['collection']) ? input['collection'] : null;
    if (!collectionRaw) bad('collection', 'is required');
    const hv = collectionRaw?.['highValueAmount'];
    if (!(hv === null || (typeof hv === 'number' && Number.isFinite(hv) && hv > 0 && hv <= SETTINGS_LIMITS.amount))) bad('collection.highValueAmount', 'must be an amount above zero, or none');
    for (const k of ['criticalOverdueDays', 'staleContactDays'] as const) {
        if (!wholeIn(collectionRaw?.[k], 1, SETTINGS_LIMITS.thresholdDays)) bad(`collection.${k}`, `must be a whole number of days from 1 to ${SETTINGS_LIMITS.thresholdDays}`);
    }
    if (!wholeIn(collectionRaw?.['promiseGraceDays'], 0, 30)) bad('collection.promiseGraceDays', 'must be a whole number of days from 0 to 30');

    if (problems.length) return { settings: null, problems };
    return {
        settings: {
            schema: RECEIVABLES_SETTINGS_SCHEMA,
            ageing: { basis: basis as AgeingConfig['basis'], boundaries: [...(ageingRaw!['boundaries'] as number[])], missingDueDate: missing as MissingDueDateRule },
            graceDays: input['graceDays'] as number,
            scope: { excludeGroups, excludeAccounts },
            collection: {
                highValueAmount: hv === null ? null : Math.round((hv as number) * 100) / 100,
                criticalOverdueDays: collectionRaw!['criticalOverdueDays'] as number,
                staleContactDays: collectionRaw!['staleContactDays'] as number,
                promiseGraceDays: collectionRaw!['promiseGraceDays'] as number,
            },
        },
        problems,
    };
}

/**
 * One line per change, in the product's words, for the audit log and the
 * version history. An unchanged save says so rather than claiming a change.
 */
export function describeSettingsChange(before: ReceivablesSettings, after: ReceivablesSettings): string[] {
    const lines: string[] = [];
    const basisWord = (b: AgeingConfig['basis']) => (b === 'bill_date' ? 'bill date' : 'due date');
    if (before.ageing.basis !== after.ageing.basis) lines.push(`Ageing by ${basisWord(before.ageing.basis)} → by ${basisWord(after.ageing.basis)}`);
    const b1 = bucketLabels(before.ageing).join(' · '), b2 = bucketLabels(after.ageing).join(' · ');
    if (before.ageing.boundaries.join(',') !== after.ageing.boundaries.join(',')) lines.push(`Buckets: ${b1} → ${b2}`);
    if (before.ageing.missingDueDate !== after.ageing.missingDueDate) {
        const w = (r: MissingDueDateRule) => (r === 'use_bill_date' ? 'due on the bill date' : 'not aged');
        lines.push(`Bills without a due date: ${w(before.ageing.missingDueDate)} → ${w(after.ageing.missingDueDate)}`);
    }
    if (before.graceDays !== after.graceDays) lines.push(`Grace after the due date: ${before.graceDays} → ${after.graceDays} days`);
    const setDiff = (a: string[], b: string[]) => b.filter(x => !a.some(y => y.toLowerCase() === x.toLowerCase()));
    for (const g of setDiff(before.scope.excludeGroups, after.scope.excludeGroups)) lines.push(`Group excluded: ${g}`);
    for (const g of setDiff(after.scope.excludeGroups, before.scope.excludeGroups)) lines.push(`Group included again: ${g}`);
    const added = setDiff(before.scope.excludeAccounts, after.scope.excludeAccounts).length;
    const removed = setDiff(after.scope.excludeAccounts, before.scope.excludeAccounts).length;
    if (added) lines.push(`${added} account${added === 1 ? '' : 's'} excluded`);
    if (removed) lines.push(`${removed} account${removed === 1 ? '' : 's'} included again`);
    const c1 = before.collection, c2 = after.collection;
    if (c1.highValueAmount !== c2.highValueAmount) lines.push(`High-value accounts: ${c1.highValueAmount ?? 'none'} → ${c2.highValueAmount ?? 'none'}`);
    if (c1.criticalOverdueDays !== c2.criticalOverdueDays) lines.push(`Critical overdue: ${c1.criticalOverdueDays} → ${c2.criticalOverdueDays} days`);
    if (c1.staleContactDays !== c2.staleContactDays) lines.push(`No contact for: ${c1.staleContactDays} → ${c2.staleContactDays} days`);
    if (c1.promiseGraceDays !== c2.promiseGraceDays) lines.push(`Promise grace: ${c1.promiseGraceDays} → ${c2.promiseGraceDays} days`);
    return lines.length ? lines : ['No change'];
}

/* ------------------------- the customization list ------------------------ */

export type SettingCategory = 'essential' | 'advanced' | 'collection';

export interface SettingDefinition {
    key: string;
    label: string;
    category: SettingCategory;
    purpose: string;
    default: string;
    where: string;
    /** Always false: a setting may change how facts are read, never the facts. */
    affectsSourceTruth: false;
    preview: string;
    audit: string;
    /** Offered on a screen today, or designed and waiting for the screens that would make it do something. */
    offered: boolean;
    whyNotYet?: string;
}

/**
 * Every customization, decided on purpose (docs/tally/18 §7). A setting a
 * screen does not yet act on is not offered — a switch that does nothing
 * tells a company something untrue (PRODUCT-UX-FOUNDATION §11).
 */
export const SETTINGS_INVENTORY: readonly SettingDefinition[] = [
    {
        key: 'ageing.basis', label: 'Age by bill date or due date', category: 'essential',
        purpose: 'Some companies chase from the invoice date, others from the date payment was due.',
        default: 'Bill date — what TallyPrime\'s Age-wise report shows by default', where: 'Connect TallyPrime › Check totals; Settings › Receivables settings',
        affectsSourceTruth: false, preview: 'Every bucket\'s bills and amount before saving', audit: 'Version saved, with who, when and the change in words', offered: true,
    },
    {
        key: 'ageing.boundaries', label: 'Ageing buckets', category: 'essential',
        purpose: 'Match the windows the company already reports in: 30-day, 45-day or its own.',
        default: '0–45 · 46–90 · 91–135 · over 135 days', where: 'Connect TallyPrime › Check totals; Settings › Receivables settings',
        affectsSourceTruth: false, preview: 'Every bucket\'s bills and amount before saving; gaps and overlaps refused', audit: 'Version saved, with the old and new buckets', offered: true,
    },
    {
        key: 'ageing.missingDueDate', label: 'Bills without a due date', category: 'advanced',
        purpose: 'Decide whether a bill TallyPrime gave no due date is due on its bill date or left out of due-date ageing.',
        default: 'Due on the bill date (TallyPrime\'s own rule)', where: 'Settings › Receivables settings (due-date ageing only)',
        affectsSourceTruth: false, preview: 'How many bills and how much this affects', audit: 'Version saved', offered: true,
    },
    {
        key: 'graceDays', label: 'Grace before overdue for collections', category: 'advanced',
        purpose: 'A company that lets customers pay a few days late can stop those bills reading as overdue for collections.',
        default: '0 days', where: 'Settings › Receivables settings',
        affectsSourceTruth: false, preview: 'Overdue for collections, and within grace, before saving', audit: 'Version saved', offered: true,
    },
    {
        key: 'scope.excludeGroups', label: 'Exclude a ledger group', category: 'advanced',
        purpose: 'Leave out ledgers kept under Sundry Debtors that are not customers to chase (staff advances, inter-company).',
        default: 'Nothing excluded', where: 'Settings › Receivables settings',
        affectsSourceTruth: false, preview: 'Accounts and amount excluded, by group', audit: 'Version saved; excluded accounts listed',
        offered: false, whyNotYet: 'Customers, Today and Reports do not yet leave excluded accounts out (they would be settled instead, which is untrue). Needs the book-side exclusion in the next release.',
    },
    {
        key: 'scope.excludeAccounts', label: 'Exclude a ledger', category: 'advanced',
        purpose: 'Leave out one ledger that is not a customer to chase.',
        default: 'Nothing excluded', where: 'Settings › Receivables settings',
        affectsSourceTruth: false, preview: 'The account and its balance', audit: 'Version saved',
        offered: false, whyNotYet: 'As for groups.',
    },
    {
        key: 'collection.highValueAmount', label: 'High-value account', category: 'collection',
        purpose: 'Say which balances deserve attention first.',
        default: 'None', where: 'Settings › Receivables settings › Collections',
        affectsSourceTruth: false, preview: 'How many accounts are at or above it', audit: 'Version saved',
        offered: false, whyNotYet: 'The collection priority is built and tested but not yet shown on Today.',
    },
    {
        key: 'collection.criticalOverdueDays', label: 'Critical overdue', category: 'collection',
        purpose: 'The age past due at which money is critical.', default: '90 days', where: 'Settings › Receivables settings › Collections',
        affectsSourceTruth: false, preview: 'Accounts it would mark', audit: 'Version saved', offered: false, whyNotYet: 'As for high value.',
    },
    {
        key: 'collection.staleContactDays', label: 'No contact for', category: 'collection',
        purpose: 'When silence on an account owing money is worth a call.', default: '14 days', where: 'Settings › Receivables settings › Collections',
        affectsSourceTruth: false, preview: 'Accounts it would mark', audit: 'Version saved', offered: false, whyNotYet: 'As for high value.',
    },
    {
        key: 'collection.promiseGraceDays', label: 'Promise grace', category: 'collection',
        purpose: 'How long after a promise\'s date before it counts as missed when nobody answered it.', default: '0 days', where: 'Settings › Receivables settings › Collections',
        affectsSourceTruth: false, preview: 'Promises it would mark', audit: 'Version saved', offered: false, whyNotYet: 'As for high value.',
    },
];

/**
 * Facts no setting, role or screen may redefine. A company can add its own
 * interpretation beside them (a Timely allocation for collections, a note, a
 * rank); it can never overwrite them.
 */
export const SOURCE_FACTS_NOT_CUSTOMIZABLE: readonly { fact: string; why: string }[] = [
    { fact: 'A voucher\'s amount', why: 'It is what TallyPrime recorded.' },
    { fact: 'A ledger\'s balance', why: 'It is TallyPrime\'s closing balance, and it reconciles to its Trial Balance.' },
    { fact: 'A bill\'s date', why: 'Ageing reads it; a setting may choose how to group ages, never move the date.' },
    { fact: 'A bill\'s due date', why: 'Grace changes when Timely calls a bill overdue for collections; the due date shown stays TallyPrime\'s.' },
    { fact: 'A bill\'s reference', why: 'It is how TallyPrime, the accountant and the customer name the bill.' },
    { fact: 'A receipt\'s amount', why: 'Money received is what TallyPrime says was received.' },
    { fact: 'A credit note or advance', why: 'A credit is shown, apart, for as long as TallyPrime holds it.' },
    { fact: 'The TallyPrime company\'s identity', why: 'The connection is bound to it; a different company is refused, never re-labelled.' },
    { fact: 'An allocation TallyPrime states', why: 'An exact allocation outranks every Timely interpretation; a Timely allocation can only sit beside an unallocated or unclear one.' },
    { fact: 'Unaged money', why: 'Money TallyPrime keeps in no bill stays unaged, with its reason. It is never put in a bucket by date or by rule.' },
];

export const SETTINGS_ARE_PRESENTATION_ONLY = SETTINGS_INVENTORY.every(s => s.affectsSourceTruth === false);

